import React, { useState, useCallback, useEffect } from 'react';
import { useInternals } from '../../context/internalContext';
import { usePenalExecution } from '../../context/penalExecutionContext';
import Process from './process';
import PenalExecution from './penalExecution';
import VisibleAccess from '../../permissions/VisibleAccess';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faEye } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import PlusCircle from '../../assets/icons/pluscircle.svg';


const formatCriminalCapitulationDisplay = (item) => {
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  const article = String(item?.capitulation || item?.art || item?.article || '').trim();
  const typology = (item?.typology || item?.tipology || item?.common_name || item?.tipificacao || item?.typification || '').toUpperCase().trim();
  if (article && typology) return `ART. ${article} - ${typology}`;
  if (article) return `ART. ${article}`;
  return typology;
};

function ProcessAndExecutionTabs({ internalId, internalDetails }) {
  const {
    getProcessInternals,
  } = useInternals();
  const {
    listPenalExecutions,
  } = usePenalExecution();

  // Estados
  const [activeTab, setActiveTab] = useState('processes'); // 'processes' ou 'executions'
  const [internalProcesses, setInternalProcesses] = useState([]);
  const [penalExecutions, setPenalExecutions] = useState([]);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  const [isLoadingExecutions, setIsLoadingExecutions] = useState(false);

  const [showProcessModal, setShowProcessModal] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [isProcessReadOnly, setIsProcessReadOnly] = useState(false);

  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [selectedExecutionId, setSelectedExecutionId] = useState(null);
  const [isExecutionReadOnly, setIsExecutionReadOnly] = useState(false);

  // Fetch processos
  const fetchProcesses = useCallback(async () => {
    if (!internalId) return;
    try {
      setIsLoadingProcesses(true);
      const data = await getProcessInternals({
        internal: internalId,
        ordering: '-created_at',
        limit: 100,
      });
      setInternalProcesses(data?.results || []);
    } catch (error) {
      toast.error('Erro ao buscar processos');
    } finally {
      setIsLoadingProcesses(false);
    }
  }, [internalId, getProcessInternals]);

  // Fetch execuções penais
  const fetchExecutions = useCallback(async () => {
    if (!internalId) return;
    try {
      setIsLoadingExecutions(true);
      const data = await listPenalExecutions({
        internal: internalId,
        limit: 100,
      });
      setPenalExecutions(data?.results || []);
    } catch (error) {
      toast.error('Erro ao buscar execuções penais');
    } finally {
      setIsLoadingExecutions(false);
    }
  }, [internalId, listPenalExecutions]);

  // Atualizar dados ao montar o componente (quando o acordeon abre)
  useEffect(() => {
    fetchProcesses();
    fetchExecutions();
  }, [fetchProcesses, fetchExecutions]);

  // Handlers de modal
  const toggleProcessModal = (processId = null, readOnly = false) => {
    setSelectedProcessId(processId);
    setIsProcessReadOnly(readOnly);
    setShowProcessModal((prev) => !prev);
  };

  const toggleExecutionModal = (executionId = null, readOnly = false) => {
    setSelectedExecutionId(executionId);
    setIsExecutionReadOnly(readOnly);
    setShowExecutionModal((prev) => !prev);
  };

  const renderEmptyState = (message) => (
    <div className="empty-state-message">
      <p>{message}</p>
    </div>
  );

  return (
    <div>
      {/* Tabs */}
      <div className="social-tabs">
        <button
          onClick={() => setActiveTab('processes')}
          className={`social-tab-button ${activeTab === 'processes' ? 'active' : ''}`}
        >
          Processos ({internalProcesses.length})
        </button>
        <button
          onClick={() => setActiveTab('executions')}
          className={`social-tab-button ${activeTab === 'executions' ? 'active' : ''}`}
        >
          Execuções Penais ({penalExecutions.length})
        </button>
      </div>

      {/* Conteúdo das Abas */}
      {activeTab === 'processes' && (
        <div>
          <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "MONITORAMENTO"]}>
            <div className='container-visibledAccess'>
              <VisibleAccess compareField="unit" compareValues={[internalDetails ? internalDetails.unit : null]}>
                <button className='btnAdcionar' onClick={() => toggleProcessModal()}>
                  <img src={PlusCircle} alt='Ícone' />
                  Adicionar novo
                </button>
              </VisibleAccess>
            </div>
          </VisibleAccess>

          <div className='content-table'>
            {isLoadingProcesses ? (
              renderEmptyState('Carregando processos...')
            ) : internalProcesses && internalProcesses.length > 0 ? (
              <table className="processes-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Regime</th>
                    <th>Execução penal</th>
                    <th className="process-col-capitulation">Capitulação Criminal</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {internalProcesses.map((process) => (
                    <tr key={process.id}>
                      <td>{process.number}</td>
                      <td>{process.regime?.name || process.regime?.description || process.regime?.regime || process.regime || '-'}</td>
                      <td>{process.execution ? (penalExecutions.find(e => e.id === process.execution)?.nu || `Exec #${process.execution}`) : '-'}</td>
                      <td className="process-col-capitulation">
                        {Array.isArray(process.criminal_capitulation) ? (
                          process.criminal_capitulation.length > 0 ? (
                            <div className="process-capitulation-cell">
                              {process.criminal_capitulation.map((item, index) => (
                                <div className="process-capitulation-item" key={`${item?.id || index}`}>
                                  {formatCriminalCapitulationDisplay(item)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            '-'
                          )
                        ) : process.criminal_capitulation ? (
                          <div className="process-capitulation-cell">
                            <div className="process-capitulation-item">
                              {formatCriminalCapitulationDisplay(process.criminal_capitulation)}
                            </div>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span className={`process-status-label ${process.is_active !== false ? 'process-status-active' : 'process-status-inactive'}`}>
                          {process.is_active !== false ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "MONITORAMENTO"]}>
                          <VisibleAccess compareField="unit" compareValues={[internalDetails ? internalDetails.unit : null]}>
                            <button onClick={() => toggleProcessModal(process.id)} className="btn-action-edit">
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </button>
                          </VisibleAccess>
                        </VisibleAccess>
                        <button onClick={() => toggleProcessModal(process.id, true)} className="btn-action-view">
                          <FontAwesomeIcon icon={faEye} className="icon-eye" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              renderEmptyState('Nenhum processo cadastrado.')
            )}
          </div>
        </div>
      )}

      {activeTab === 'executions' && (
        <div>
          <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "MONITORAMENTO"]}>
            <div className='container-visibledAccess'>
              <VisibleAccess compareField="unit" compareValues={[internalDetails ? internalDetails.unit : null]}>
                <button className='btnAdcionar' onClick={() => toggleExecutionModal()}>
                  <img src={PlusCircle} alt='Ícone' />
                  Adicionar novo
                </button>
              </VisibleAccess>
            </div>
          </VisibleAccess>

          <div className='content-table'>
            {isLoadingExecutions ? (
              renderEmptyState('Carregando execuções penais...')
            ) : penalExecutions && penalExecutions.length > 0 ? (
              <table className="processes-table">
                <thead>
                  <tr>
                    <th>numero</th>
                    <th>Regime Atual</th>
                    <th>Pena (Anos/Meses/Dias)</th>
                    <th>Ativo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {penalExecutions.map((execution) => (
                    <tr key={execution.id}>
                      <td>{execution.nu}</td>
                      <td>{execution.current_regime_name || execution.current_regime || '-'}</td>
                      <td>{execution.years || 0}a/{execution.months || 0}m/{execution.days || 0}d</td>
                      <td>{execution.is_active ? 'Sim' : 'Não'}</td>
                      <td>
                        <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "MONITORAMENTO"]}>
                          <VisibleAccess compareField="unit" compareValues={[internalDetails ? internalDetails.unit : null]}>
                            <button onClick={() => toggleExecutionModal(execution.id)} className="btn-action-edit">
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </button>
                          </VisibleAccess>
                        </VisibleAccess>

                        <button onClick={() => toggleExecutionModal(execution.id, true)} className="btn-action-view">
                          <FontAwesomeIcon icon={faEye} className="icon-eye" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              renderEmptyState('Nenhuma execução penal cadastrada.')
            )}
          </div>
        </div>
      )}

      {/* Modals */}
            {showProcessModal && (
        <Process
          internalId={internalId}
          processId={selectedProcessId}
          isOpen={!!showProcessModal}
          closeModal={() => toggleProcessModal()}
          readOnly={isProcessReadOnly}
          onSaveSuccess={() => {
            fetchProcesses();
          }}
        />
      )}

            {showExecutionModal && (
        <PenalExecution
          internalId={internalId}
          executionId={selectedExecutionId}
          isOpen={!!showExecutionModal}
          closeModal={() => toggleExecutionModal()}
          readOnly={isExecutionReadOnly}
          onSaveSuccess={() => {
            fetchExecutions();
          }}
        />
      )}
    </div>
  );
}

export default ProcessAndExecutionTabs;
